<template>
  <div class="entity-list-page quotes-list-page">
    <header class="list-header">
      <div>
        <router-link to="/settings/marketing" class="back-link">← Marketing</router-link>
        <h1>Saved quotes</h1>
        <p class="list-sub">Manage, edit, and export quotes and proposals.</p>
      </div>
      <div class="header-actions">
        <router-link to="/quote-builder" class="btn btn-primary">
          <i class="fas fa-plus" aria-hidden="true"></i> Create quote
        </router-link>
      </div>
    </header>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search by client name..."
          @input="debouncedSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="statusFilter" @change="loadQuotes">
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="PENDING_REVIEW">Pending Review</option>
          <option value="FINALIZED">Finalized</option>
          <option value="SENT">Sent</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="DECLINED">Declined</option>
        </select>
        <select v-model="typeFilter" @change="loadQuotes">
          <option value="">All Types</option>
          <option value="AGENT">Agent Proposal</option>
          <option value="CARRIER">Carrier Quote</option>
          <option value="PROPOSAL">Comprehensive Proposal</option>
          <option value="SIMPLE">Simple Quote</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading quotes...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="quotes.length === 0" class="empty-state">
      <i class="fas fa-file-alt"></i>
      <h3>No quotes found</h3>
      <p v-if="searchQuery || statusFilter || typeFilter">
        Try adjusting your filters or search query.
      </p>
      <p v-else>
        Create your first quote to get started.
      </p>
      <router-link to="/quote-builder" class="btn btn-primary">
        <i class="fas fa-plus"></i> Create New Quote
      </router-link>
    </div>

    <!-- Quotes Grid -->
    <div v-else class="quotes-grid">
      <div 
        v-for="quote in quotes" 
        :key="quote.id" 
        class="quote-card"
        :class="[`status-${quote.status.toLowerCase()}`]"
      >
        <div class="quote-header">
          <span class="quote-number">{{ quote.quote_number }}</span>
          <span class="quote-type" :class="quote.quote_type.toLowerCase()">
            {{ quote.quote_type_display }}
          </span>
        </div>
        
        <h3 class="client-name">{{ quote.client_name }}</h3>
        
        <div class="quote-meta">
          <span class="meta-item">
            <i class="fas fa-calendar"></i>
            {{ formatDate(quote.updated_at) }}
          </span>
          <span class="meta-item" v-if="quote.contact_name">
            <i class="fas fa-user"></i>
            {{ quote.contact_name }}
          </span>
        </div>
        
        <div class="quote-status">
          <span class="status-badge" :class="quote.status.toLowerCase()">
            {{ quote.status_display }}
          </span>
        </div>
        
        <div class="quote-actions">
          <router-link 
            :to="`/quote-editor/${quote.id}`" 
            class="btn btn-sm btn-primary"
          >
            <i class="fas fa-edit"></i> Edit
          </router-link>
          <button @click="duplicateQuote(quote)" class="btn btn-sm btn-outline">
            <i class="fas fa-copy"></i>
          </button>
          <button 
            v-if="quote.status === 'DRAFT'" 
            @click="archiveQuote(quote)" 
            class="btn btn-sm btn-danger-outline"
          >
            <i class="fas fa-archive"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle"></i>
      {{ error }}
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import api from '@/services/api';
import { debounce } from 'lodash-es';

export default {
  name: 'QuotesList',
  setup() {
    const quotes = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const statusFilter = ref('');
    const typeFilter = ref('');

    const loadQuotes = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const params = {};
        if (searchQuery.value) params.search = searchQuery.value;
        if (statusFilter.value) params.status = statusFilter.value;
        if (typeFilter.value) params.quote_type = typeFilter.value;
        
        const response = await api.get('/quotes/', { params });
        quotes.value = response.data.quotes;
      } catch (err) {
        console.error('Error loading quotes:', err);
        error.value = 'Failed to load quotes';
      } finally {
        loading.value = false;
      }
    };

    const debouncedSearch = debounce(() => {
      loadQuotes();
    }, 300);

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    const duplicateQuote = async (quote) => {
      if (!confirm(`Duplicate quote for ${quote.client_name}?`)) return;
      
      try {
        const response = await api.post(`/quotes/${quote.id}/duplicate/`);
        quotes.value.unshift(response.data.quote);
        alert(`Quote duplicated as ${response.data.quote.quote_number}`);
      } catch (err) {
        console.error('Error duplicating quote:', err);
        alert('Failed to duplicate quote');
      }
    };

    const archiveQuote = async (quote) => {
      if (!confirm(`Archive quote ${quote.quote_number}?`)) return;
      
      try {
        await api.delete(`/quotes/${quote.id}/`);
        quotes.value = quotes.value.filter(q => q.id !== quote.id);
      } catch (err) {
        console.error('Error archiving quote:', err);
        alert('Failed to archive quote');
      }
    };

    onMounted(() => {
      loadQuotes();
    });

    return {
      quotes,
      loading,
      error,
      searchQuery,
      statusFilter,
      typeFilter,
      loadQuotes,
      debouncedSearch,
      formatDate,
      duplicateQuote,
      archiveQuote
    };
  }
};
</script>

<style scoped>
.quotes-list-page {
  /* Page chrome: global .entity-list-page */
  padding-bottom: 2.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.subtitle {
  color: #666;
  margin: 0.5rem 0 0 0;
}

.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
}

.filter-group {
  display: flex;
  gap: 0.75rem;
}

.filter-group select {
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 0.9rem;
  min-width: 150px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
}

.loading-state i,
.empty-state i {
  font-size: 3rem;
  color: #667eea;
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 1rem 0 0.5rem;
  color: #1a1a2e;
}

.quotes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.quote-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.quote-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.quote-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.quote-number {
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.85rem;
  color: #666;
  background: #f0f0f5;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.quote-type {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.quote-type.agent {
  background: #e8f5e9;
  color: #2e7d32;
}

.quote-type.carrier {
  background: #e3f2fd;
  color: #1565c0;
}

.quote-type.proposal {
  background: #fce4ec;
  color: #c2185b;
}

.quote-type.simple {
  background: #f5f5f5;
  color: #616161;
}

.client-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 1rem 0;
}

.quote-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: #666;
}

.meta-item i {
  margin-right: 0.4rem;
  color: #999;
}

.quote-status {
  margin-bottom: 1rem;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.draft {
  background: #fff3cd;
  color: #856404;
}

.status-badge.pending_review {
  background: #e3f2fd;
  color: #1565c0;
}

.status-badge.finalized {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.sent {
  background: #f3e5f5;
  color: #7b1fa2;
}

.status-badge.accepted {
  background: #c8e6c9;
  color: #1b5e20;
}

.status-badge.declined {
  background: #ffcdd2;
  color: #c62828;
}

.status-badge.archived {
  background: #f5f5f5;
  color: #9e9e9e;
}

.quote-actions {
  display: flex;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-sm {
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5568d3;
}

.btn-outline {
  background: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.btn-outline:hover {
  background: #f5f5f5;
  border-color: #ccc;
}

.btn-danger-outline {
  background: transparent;
  border: 1px solid #ffcdd2;
  color: #c62828;
}

.btn-danger-outline:hover {
  background: #ffebee;
}

.error-message {
  margin-top: 1rem;
  padding: 1rem;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  color: #c53030;
}

.error-message i {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    max-width: none;
  }
  
  .filter-group {
    flex-direction: column;
  }
  
  .quotes-grid {
    grid-template-columns: 1fr;
  }
}
</style>

